
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  PropsWithChildren,
} from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Client } from "@/components/programs/ClientSelection";
import { TierFormData } from "./program-creation/types";
import { useProgramOperations } from "./program-operations";
import { useClientOperations } from "./client-operations";

interface ProgramCreationContextType {
  step: number;
  clients: Client[];
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  isLoading: boolean;
  isSaving: boolean;
  handleNextStep: () => void;
  handlePreviousStep: () => void;
  updateClientDetails: (data: z.infer<typeof clientFormSchema>) => Promise<void>;
  handleSaveProgram: (tierData: TierFormData) => Promise<void>;
  resetSession: () => void;
}

const ProgramCreationContext = createContext<ProgramCreationContextType>(
  {} as ProgramCreationContextType
);

export const useProgramCreation = () => useContext(ProgramCreationContext);

export const ProgramCreationProvider = (props: PropsWithChildren) => {
  // Try to restore step from session storage when component mounts
  const savedStepStr = localStorage.getItem('programCreationStep');
  const savedClientStr = localStorage.getItem('selectedClient');
  
  const initialStep = savedStepStr ? parseInt(savedStepStr, 10) : 1;
  const initialClient = savedClientStr ? JSON.parse(savedClientStr) : null;
  
  const [step, setStep] = useState(initialStep);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(initialClient);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSaveProgram: saveProgram } = useProgramOperations();
  const { updateClientDetails: updateClient } = useClientOperations();

  // Save step to session storage when it changes
  useEffect(() => {
    localStorage.setItem('programCreationStep', step.toString());
  }, [step]);
  
  // Save selected client to session storage when it changes
  useEffect(() => {
    if (selectedClient) {
      localStorage.setItem('selectedClient', JSON.stringify(selectedClient));
    }
  }, [selectedClient]);

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('coach_id', user.id);

        if (error) {
          console.error('Error fetching clients:', error);
          return;
        }

        // Transform data to match Client interface
        const transformedClients: Client[] = data.map((client) => ({
          id: client.client_id,
          name: client.full_name || 'Anonymous Client',
          avatar: '/placeholder.svg',
          email: client.email || '',
          phone: client.phone || '',
          age: client.age || 0,
          gender: client.gender || '',
          goals: client.goals || '',
          notes: client.notes || '',
          // Add required properties from Client interface
          joinDate: new Date().toISOString(),
          tier: 'Beginner' as 'Beginner' | 'Intermediate' | 'Advanced',
          status: 'active',
          currentPlan: {
            name: 'No active plan',
            progress: {
              current: 0,
              total: 0
            }
          },
          progress: {
            completionRate: [],
            performance: []
          }
        }));

        setClients(transformedClients);
      }
    } catch (error) {
      console.error('Error in fetchClients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    setStep((prev) => {
      const newStep = prev + 1;
      localStorage.setItem('programCreationStep', newStep.toString());
      return newStep;
    });
  };

  const handlePreviousStep = () => {
    setStep((prev) => {
      const newStep = Math.max(1, prev - 1);
      localStorage.setItem('programCreationStep', newStep.toString());
      return newStep;
    });
  };

  const updateClientDetails = async (data: z.infer<typeof clientFormSchema>) => {
    setIsSaving(true);
    try {
      await updateClient(selectedClient?.id, data);
      
      // Update local client data
      if (selectedClient) {
        const updatedClient = { 
          ...selectedClient, 
          ...data 
        };
        setSelectedClient(updatedClient);
        
        setClients(clients.map(client => 
          client.id === updatedClient.id ? updatedClient : client
        ));
      }
    } catch (error) {
      console.error('Error in updateClientDetails:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProgram = async (tierData: TierFormData) => {
    setIsSaving(true);
    try {
      await saveProgram(tierData, selectedClient, setIsSaving);
      // Clear session data after successful save
      resetSession();
    } catch (error) {
      console.error('Error in handleSaveProgram:', error);
      setIsSaving(false);
    }
  };
  
  // New function to reset all session data
  const resetSession = () => {
    localStorage.removeItem('programCreationStep');
    localStorage.removeItem('selectedClient');
    localStorage.removeItem('programData');
    localStorage.removeItem('tierData');
    localStorage.removeItem('preferencesData');
    setStep(1);
    setSelectedClient(null);
  };

  return (
    <ProgramCreationContext.Provider
      value={{
        step,
        clients,
        selectedClient,
        setSelectedClient,
        isLoading,
        isSaving,
        handleNextStep,
        handlePreviousStep,
        updateClientDetails,
        handleSaveProgram,
        resetSession,
      }}
    >
      {props.children}
    </ProgramCreationContext.Provider>
  );
};

export { ProgramCreationContext };

// Zod schema for client form
export const clientFormSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Invalid email address.",
  }).optional(),
  phone: z.string().optional(),
  age: z.number().optional(),
  gender: z.enum(["male", "female", "other"]).optional(),
  goals: z.string().optional(),
  notes: z.string().optional()
});
